import React, { useState } from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  GetClearanceStatus_getClearanceStatus_pagination,
  GetClearanceStatus_getClearanceStatus_users,
  ClearanceStatusType,
  useClearanceSetup,
} from '@modmd/data'
import { COLOR, DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import Label from 'components/Label'
import Button from 'components/Button'
import { Text } from 'components/Typography'
import { Spinner } from 'components/Spinner'
import { Box } from 'components/Layout'
import Icon from 'components/Icon'
import { Checkbox } from 'components/Checkbox'
import { ClearanceStatusLabels, VaccineStatusLabels, TestsStatusLabels } from 'utils/labels'
import { WarningDialog } from './WarningDialog'

interface ColumnLabels {
  userChecked?: string | null
  clearanceId?: string | null
  name?: string | null
  questionnaire?: string | null
  tests?: string | null
  vaccine?: string | null
  status?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  data: GetClearanceStatus_getClearanceStatus_users[]
  pagination?: GetClearanceStatus_getClearanceStatus_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (data: GetClearanceStatus_getClearanceStatus_users) => void
  handleRunClearance: (userId: string, groupId: string) => void
  isAddClearanceStatusLoading: boolean
  handleDenyClearance: (clearanceId: string, groupId: string) => void
  isDenyClearanceStatusLoading: boolean
  handleApproveClearance: (clearanceId: string, groupId: string) => void
  isApproveClearanceStatusLoading: boolean
  companyId: string
  handleChangeSelectedMembers: (
    userId: string,
    clearanceId: string | null,
    clearanceCheckStatus: ClearanceStatusType,
    entranceStatus: ClearanceStatusType
  ) => void
  selectedMembers: { userId: string; clearanceId: string | null }[]
  handleResendEmailQuestionnaire: (userId: string, groupId: string) => void
  isResendEmailQuestionnaireLoading: boolean
}

const PAGE_LENGTH = 10

export const CompanyEventClearanceDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  data,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
  handleRunClearance,
  isAddClearanceStatusLoading,
  handleDenyClearance,
  handleApproveClearance,
  isDenyClearanceStatusLoading,
  isApproveClearanceStatusLoading,
  companyId,
  handleChangeSelectedMembers,
  selectedMembers,
  handleResendEmailQuestionnaire,
  isResendEmailQuestionnaireLoading,
}) => {
  const { data: clearanceSetup, loading: isClearanceStatusLoading } = useClearanceSetup({
    companyId,
  })
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState<{
    travelStatus: ClearanceStatusType | null
    exposureStatus: ClearanceStatusType | null
  } | null>(null)

  const defaultColumnLabels = {
    userChecked: '',
    clearanceId: 'Id',
    name: 'Name',
    questionnaire: clearanceSetup?.getClearanceSetup?.isQuestionnaireOn ? 'Questionnaire' : null,
    tests: clearanceSetup?.getClearanceSetup?.isTestOn ? 'Tests' : null,
    vaccine: clearanceSetup?.getClearanceSetup?.isVaccinationOn ? 'Vaccine' : null,
    status: 'Status',
  }

  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(
    () => `50px 70px 2fr ${' 1fr'.repeat(numOfColumns - 3)}`,
    [numOfColumns]
  )

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading || isClearanceStatusLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={data}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row alignContent="start" gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.userChecked) ? (
                <Datalist.HeaderCell label={columnLabels.userChecked} />
              ) : null}
              {!isNull(columnLabels.clearanceId) ? (
                <Datalist.HeaderCell label={columnLabels.clearanceId} />
              ) : null}
              {!isNull(columnLabels.name) ? (
                <Datalist.HeaderCell label={columnLabels.name} />
              ) : null}
              {!isNull(columnLabels.questionnaire) ? (
                <Datalist.HeaderCell label={columnLabels.questionnaire} />
              ) : null}
              {!isNull(columnLabels.tests) ? (
                <Datalist.HeaderCell label={columnLabels.tests} />
              ) : null}
              {!isNull(columnLabels.vaccine) ? (
                <Datalist.HeaderCell label={columnLabels.vaccine} />
              ) : null}
              {!isNull(columnLabels.status) ? (
                <Datalist.HeaderCell label={columnLabels.status} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const {
              firstName,
              lastName,
              clearanceId,
              questionnaireStatus,
              testCheckedStatus,
              vaccinationStatus,
              clearanceCheckStatus,
              name,
              userId,
              groupId,
              entranceStatus,
              travelStatus,
              exposureStatus,
            } = data

            const fullName = `${firstName || ''} ${lastName || ''}`
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.userChecked) ? (
                  <Datalist.Cell textAlign="start" label={columnLabels.userChecked}>
                    <Checkbox
                      checked={
                        selectedMembers &&
                        selectedMembers.some(
                          (member) => member.userId === userId && member.clearanceId === clearanceId
                        )
                      }
                      onChange={() =>
                        handleChangeSelectedMembers(
                          userId,
                          clearanceId,
                          clearanceCheckStatus!,
                          entranceStatus!
                        )
                      }
                      label=""
                      hasBottomContent={false}
                    />
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.clearanceId) ? (
                  <Datalist.Cell textAlign="start" label={columnLabels.clearanceId}>
                    {clearanceId || '-'}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell textAlign="start" label={columnLabels.name}>
                    <p>{fullName}</p>
                    <Label indicator="grayDark">Group: {name}</Label>
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.questionnaire) ? (
                  <Datalist.Cell textAlign="start" label={columnLabels.questionnaire}>
                    {questionnaireStatus === ClearanceStatusType.NotSubmitted && (
                      <Button
                        onClick={() => handleResendEmailQuestionnaire(userId, groupId)}
                        appearance="link"
                        size="large"
                        disabled={isResendEmailQuestionnaireLoading}
                      >
                        <Text color="primary" fontSize="s">
                          {isResendEmailQuestionnaireLoading ? (
                            <Spinner size="small" color={COLOR.brand} />
                          ) : (
                            `Resend email`
                          )}
                        </Text>
                      </Button>
                    )}
                    {!questionnaireStatus && '-'}
                    {questionnaireStatus === ClearanceStatusType.Warning && (
                      <Button
                        onClick={() => setIsWarningDialogOpen({ travelStatus, exposureStatus })}
                        appearance="link"
                        size="large"
                        disabled={isAddClearanceStatusLoading}
                      >
                        <Text color="primary" fontSize="s">
                          {
                            ClearanceStatusLabels[
                              questionnaireStatus as keyof typeof ClearanceStatusLabels
                            ]
                          }
                        </Text>
                      </Button>
                    )}
                    {questionnaireStatus !== ClearanceStatusType.Warning &&
                      questionnaireStatus !== ClearanceStatusType.NotSubmitted &&
                      ClearanceStatusLabels[
                        questionnaireStatus as keyof typeof ClearanceStatusLabels
                      ]}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.tests) ? (
                  <Datalist.Cell textAlign="start" label={columnLabels.tests}>
                    {testCheckedStatus
                      ? TestsStatusLabels[testCheckedStatus as keyof typeof TestsStatusLabels]
                      : '-'}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.vaccine) ? (
                  <Datalist.Cell textAlign="start" label={columnLabels.vaccine}>
                    {vaccinationStatus
                      ? VaccineStatusLabels[vaccinationStatus as keyof typeof VaccineStatusLabels]
                      : '-'}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.status) ? (
                  <Datalist.Cell textAlign="start" label={columnLabels.status}>
                    {!clearanceId && (
                      <Box>
                        <Button
                          onClick={() => handleRunClearance(userId, groupId)}
                          appearance="link"
                          size="large"
                          disabled={isAddClearanceStatusLoading}
                        >
                          {isAddClearanceStatusLoading ? (
                            <Spinner size="small" color={COLOR.brand} />
                          ) : (
                            <Text color="primary" fontSize="s">
                              Run FastPass
                            </Text>
                          )}
                        </Button>
                      </Box>
                    )}
                    {!!clearanceId &&
                      clearanceCheckStatus === ClearanceStatusType.NotSubmitted &&
                      entranceStatus === ClearanceStatusType.NotSubmitted && (
                        <Box>
                          <Button
                            onClick={() => handleApproveClearance(clearanceId, groupId)}
                            appearance="link"
                            size="large"
                            disabled={isApproveClearanceStatusLoading}
                          >
                            {isApproveClearanceStatusLoading ? (
                              <Spinner size="small" color={COLOR.brand} />
                            ) : (
                              <Icon.Checkmark size="XL" color={COLOR.green} />
                            )}
                          </Button>
                          <Button
                            onClick={() => handleDenyClearance(clearanceId, groupId)}
                            appearance="link"
                            size="large"
                            disabled={isDenyClearanceStatusLoading}
                          >
                            {isDenyClearanceStatusLoading ? (
                              <Spinner size="small" color={COLOR.brand} />
                            ) : (
                              <Icon.Close size="XL" color={COLOR.danger} />
                            )}
                          </Button>
                        </Box>
                      )}
                    {!!clearanceId && clearanceCheckStatus === ClearanceStatusType.Cleared && (
                      <Text color={COLOR.grayDark} fontSize="s">
                        Auto approved
                      </Text>
                    )}
                    {!!clearanceId && clearanceCheckStatus === ClearanceStatusType.NotCleared && (
                      <Text color={COLOR.grayDark} fontSize="s">
                        Auto denied
                      </Text>
                    )}
                    {!!clearanceId && entranceStatus !== ClearanceStatusType.NotSubmitted && (
                      <Text color={COLOR.grayDark} fontSize="s">
                        {ClearanceStatusLabels[entranceStatus!]}
                      </Text>
                    )}
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
      <WarningDialog
        isWarningDialogOpen={isWarningDialogOpen}
        setIsWarningDialogOpen={setIsWarningDialogOpen}
      />
    </>
  )
}
